import {
  Badge,
  HStack,
  IconButton,
  Switch,
  Td,
  Text,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { getDomainLabel } from 'domain/configs/domain-labels';
import { Config } from 'domain/configs/models/config';
import { useGlobalContext } from 'GlobalContext';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { IoCopyOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { editConfig } from 'services/config';
import { Mixpanel } from 'services/mixpanel';
import ProtectedComponent from 'shared/components/ProtectedComponent';
import { copyToClipboard } from 'shared/helpers/clipboard';
import { usePermissions } from 'shared/hooks/usePermissions';
import { useAppTabContext } from '../../../AppContext';
import { getFeesFromDriver } from '../../IntegratedPayment/helpers/feesMapping';
import EditModal from './EditModal';

interface ConfigRowProps {
  config: Config;
}

const ConfigRow: React.FC<ConfigRowProps> = ({ config }) => {
  const { d } = usePermissions();
  const { confirm } = useGlobalContext();
  const { app, setLoadingConfigs, updateConfig, getDriver } =
    useAppTabContext();
  const { customer } = useCustomerContext();

  const getRequiredPermission = () => {
    if (config.name === 'feature_toggle_yooga_additional_tax') {
      return 'installation-card-configs-additional_tax-update';
    }

    if (config.name === 'feature_toggle_send_promo_clube') {
      return 'installation-card-configs-promo_clube-update';
    }

    return 'installation-card-configs-update';
  };

  const requiredPermission = getRequiredPermission();

  const [openEditModal, setOpenEditModal] = useState(false);

  const copyToken = () =>
    copyToClipboard(String(config.trueValue), 'Token copiado');

  const openEdit = () => {
    Mixpanel.track('customers-app-configs-opened-edit-value');
    setOpenEditModal(true);
  };

  const getClubeYoogaPayload = () => {
    const fees = getDriver ? getFeesFromDriver(getDriver) : [];
    return {
      customerId: customer?.id,
      marketplaceFeesOld: fees,
    };
  };

  const buildEditPayload = (value: boolean | number | string) => {
    let processedValue: boolean | number | string = value;
    if (config.type === 'BOOLEAN' && typeof value === 'string') {
      processedValue = value === 'true';
    }

    const payload = {
      name: config.name,
      value: processedValue,
    };

    if (config.name === 'feature_toggle_yooga_additional_tax') {
      return {
        ...payload,
        ...getClubeYoogaPayload(),
      };
    }

    return payload;
  };

  const onChangeSwitch = () => {
    if (!config.readonly) {
      if (
        config.name === 'feature_toggle_yooga_additional_tax' &&
        config.booleanValue &&
        d('installation-card-configs-additional_tax-update')
      ) {
        toast.error('Você não tem permissão para desativar esta configuração');
        return;
      }
      confirm(
        `${config.booleanValue ? 'Desabilitar' : 'Habilitar'} configuração`,
        `${config.name !== 'feature_toggle_yooga_additional_tax' ? 'Deseja continuar?' : 'ATENÇÃO: Ao editar esta configuração, a taxa do PIX será alterada, além das seguintes configs: "delivery_enable_driver_registration", "kds_enabled", "modulo_motoboy_app", "yooga_whatsapp_enabled" e "zapturbo_enabled". Deseja continuar?'}`,
        () => {
          setLoadingConfigs(true);

          Mixpanel.track('customers-app-configs-clicked-toggle');

          const newValue = config.getTrueTypeOf(!config.booleanValue);

          editConfig(String(app.idi), buildEditPayload(newValue))
            .then((response) => {
              toast.success(
                `Configuração ${
                  config.booleanValue ? 'desabilitada' : 'habilitada'
                }`,
              );
              updateConfig(response.data.configs);
            })
            .catch(() =>
              toast.error(
                `Falha ao ${
                  config.booleanValue ? 'desabilitar' : 'habilitar'
                } configuração`,
              ),
            )
            .finally(() => setLoadingConfigs(false));
        },
      );
    }
  };
  return (
    <Tr>
      <EditModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        config={config}
      />
      <Td textAlign="center">{getDomainLabel(config.domain)}</Td>
      <Td>{config.name}</Td>
      <Td>
        <Badge
          rounded="full"
          fontWeight="500"
          w="100%"
          px="0.5rem"
          textAlign="center"
          colorScheme={config.type === 'BOOLEAN' ? 'yooga' : 'gray'}
        >
          {config.type === 'BOOLEAN' ? 'Botão habilitar' : 'Valor manual'}
        </Badge>
      </Td>
      <Td>
        {(config.type === 'TOKEN' ||
          config.type === 'SELECTION' ||
          config.type === 'NUMBER') &&
          !!config.trueValue && (
            <HStack>
              <Text overflow="hidden" textOverflow="ellipsis" maxW="5rem">
                {config.trueValue}
              </Text>
              <IconButton
                aria-label="Copy value"
                size="sm"
                variant="link"
                onClick={copyToken}
                icon={<IoCopyOutline />}
              />
            </HStack>
          )}
      </Td>
      <Td>
        <Tooltip label={config.description}>
          <Text
            overflow="hidden"
            textOverflow="ellipsis"
            fontSize="0.8rem"
            maxW="10rem"
          >
            {config.description}
          </Text>
        </Tooltip>
      </Td>
      <Td textAlign="center">
        {(config.type === 'BOOLEAN' || config.type === 'NUMBER') &&
          (config.name === 'feature_toggle_yooga_additional_tax' ? (
            <HStack>
              <Switch
                size="sm"
                isChecked={config.booleanValue}
                onChange={onChangeSwitch}
                isDisabled={
                  config.readonly ||
                  (config.booleanValue &&
                    d('installation-card-configs-additional_tax-update'))
                }
              />
              <Text fontSize="0.8rem">
                {config.booleanValue ? 'Ativado' : 'Desativado'}
              </Text>
            </HStack>
          ) : (
            <ProtectedComponent permission={requiredPermission}>
              <HStack>
                <Switch
                  size="sm"
                  isChecked={config.booleanValue}
                  onChange={onChangeSwitch}
                  isDisabled={d(requiredPermission) || config.readonly}
                />
                <Text fontSize="0.8rem">
                  {config.booleanValue ? 'Ativado' : 'Desativado'}
                </Text>
              </HStack>
            </ProtectedComponent>
          ))}
      </Td>
      <Td textAlign="center">
        <ProtectedComponent permission={requiredPermission}>
          <IconButton
            aria-label="Ver detalhes"
            icon={<FaExternalLinkAlt />}
            variant="ghost"
            size="sm"
            onClick={openEdit}
            isDisabled={
              (config.type !== 'TOKEN' && config.type !== 'SELECTION') ||
              d(requiredPermission)
            }
            visibility={config.type !== 'TOKEN' ? 'hidden' : 'visible'}
          />
        </ProtectedComponent>
      </Td>
    </Tr>
  );
};

export default ConfigRow;
